import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Empty, Image, Segmented, Space, Tag, Button, Input, Popconfirm, Typography, App, Spin } from 'antd';
import { CopyOutlined, DeleteOutlined, DownloadOutlined, FileImageOutlined } from '@ant-design/icons';
import { useGenerations, usePatchTags, useDeleteGeneration } from '@/hooks/useMedia';
import type { MediaGeneration } from '@/types/media';

export function MediaLibrary({ refreshKey }: { refreshKey: number }) {
  const { message } = App.useApp();
  const [modality, setModality] = useState<string>('');
  const q = useGenerations({ modality: modality || undefined, status: 'succeeded', pageSize: 36 });
  const patch = usePatchTags();
  const del = useDeleteGeneration();

  useEffect(() => { q.refetch(); /* refresh when a new gen completes */ }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const items = q.data?.data ?? [];

  return (
    <Card title="Media library" extra={
      <Segmented size="small" value={modality} onChange={(v) => setModality(v as string)}
        options={[{ label: 'All', value: '' }, { label: 'Image', value: 'image' }, { label: 'Video', value: 'video' }]} />
    }>
      {q.isLoading ? <Spin /> : items.length === 0 ? <Empty description="No media yet" /> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
          {items.map((g) => <MediaCard key={g.id} g={g}
            onTags={async (tags) => {
              try { await patch.mutateAsync({ id: g.id, tags }); }
              catch (e) { message.error((e as Error).message); }
            }}
            onCopyPrompt={async () => {
              try { await navigator.clipboard.writeText(g.prompt); message.success('Prompt copied'); }
              catch (e) { message.error((e as Error).message); }
            }}
            onDelete={async () => {
              try { await del.mutateAsync(g.id); message.success('Deleted'); }
              catch (e) { message.error((e as Error).message); }
            }} />)}
        </div>
      )}
    </Card>
  );
}

function MediaCard({ g, onTags, onCopyPrompt, onDelete }: { g: MediaGeneration; onTags: (t: string[]) => void; onCopyPrompt: () => void; onDelete: () => void }) {
  const asset = g.results[0];
  const [tagInput, setTagInput] = useState('');
  return (
    <Card size="small" styles={{ body: { padding: 8 } }}
      cover={asset ? (g.modality === 'video'
        ? <video src={asset.url} controls style={{ width: '100%', height: 140, objectFit: 'cover', background: '#000' }} />
        : <Image src={asset.url} height={140} style={{ objectFit: 'cover' }} />) : null}>
      <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ fontSize: 12, marginBottom: 6 }}>{g.prompt}</Typography.Paragraph>
      <Space size={4} wrap style={{ marginBottom: 6 }}>
        {g.sourceTopicNo && <Tag color="cyan">program</Tag>}
        {(g.tags ?? []).map((t) => <Tag key={t} closable onClose={() => onTags((g.tags ?? []).filter((x) => x !== t))}>{t}</Tag>)}
      </Space>
      <Input size="small" placeholder="add tag ⏎" value={tagInput} onChange={(e) => setTagInput(e.target.value)}
        onPressEnter={() => { if (tagInput.trim()) { onTags([...new Set([...(g.tags ?? []), tagInput.trim()])]); setTagInput(''); } }} style={{ marginBottom: 6 }} />
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>${g.costUsd ?? 0}</Typography.Text>
        <Space>
          <Button size="small" icon={<CopyOutlined />} onClick={onCopyPrompt} />
          {asset && <Button size="small" icon={<DownloadOutlined />} href={asset.url} target="_blank" />}
          {g.modality === 'image' && asset && (
            <Link to={`/flyers/templates/new?bg=${g.id}${g.sourceTopicNo ? `&topic=${encodeURIComponent(g.sourceTopicNo)}` : ''}`}>
              <Button size="small" icon={<FileImageOutlined />} title="Use as flyer background" />
            </Link>
          )}
          <Popconfirm title="Delete?" onConfirm={onDelete}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
        </Space>
      </Space>
    </Card>
  );
}
